import { ContentAdd } from '@fs/zion-icon'
import { Button, DialogOverlay, DialogOverlayActions, DialogOverlayFooter } from '@fs/zion-ui'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../../../hooks/useNotifications'
import {
  createTreeDataCouple,
  createTreeDataParentChildRelationship,
  createTreeDataPerson,
  getPerson,
} from '../../../../services/personService'
import { getLastPathElement, getPidFromIdentifier, setIdInIdentifiers } from '../../../../util/StringUtil'
import { deepCopy } from '../../../../util/Utils'
import CreateTreePersonTable from './CreateTreePersonTable'

const hasExtendedDetail = (templePlatform, role) => {
  return (
    templePlatform &&
    templePlatform.ctrs &&
    templePlatform.ctrs[0] &&
    templePlatform.ctrs[0].ctrExtendedDetail &&
    templePlatform.ctrs[0].ctrExtendedDetail[0] &&
    templePlatform.ctrs[0].ctrExtendedDetail[0][role.toLowerCase()]
  )
}

export default function CreateTreePersonDialog({
  templePlatform,
  openModifyCtrDialog,
  setModifiedPersons,
  ...overlay
}) {
  const { t } = useTranslation()
  const { error, ok } = useNotifications()
  const [selectedPersonRoles, setSelectedPersonRoles] = useState([])
  const [creatingEntities, setCreatingEntities] = useState(false)
  const treeEntityRoles = ['Principal', 'Father', 'Mother', 'Wife']

  const potentialTreeEntities = treeEntityRoles
    .filter((role) => hasExtendedDetail(templePlatform, role))
    .map((role) => {
      return {
        role,
        person: templePlatform.persons.find(
          (person) => person.id === templePlatform.ctrs[0].ctrExtendedDetail[0][role.toLowerCase()].resourceId
        ),
      }
    })
  const cancelBtnOnClick = () => {
    overlay.close()
  }
  const createBtnOnClick = async () => {
    setCreatingEntities(true)
    const unselectedPersons = potentialTreeEntities.filter((entity) => !selectedPersonRoles.includes(entity.role))
    await createTreeEntity(
      error,
      ok,
      t,
      templePlatform,
      setModifiedPersons,
      selectedPersonRoles,
      unselectedPersons,
      openModifyCtrDialog
    )
    setCreatingEntities(false)
    overlay.close()
  }

  return (
    <DialogOverlay
      {...overlay}
      title={t('ctr.summary.createTreePerson.dialogHeading', 'Create Tree Entities')}
      subtitle={t(
        'ctr.summary.createTreePerson.dialogSubheading',
        'Create new TF entities and relationships, replace pid(s) in CTR'
      )}
      closeOnClickAway={false}
    >
      <CreateTreePersonTable
        potentialTreeEntities={potentialTreeEntities}
        onPersonSelectionChange={setSelectedPersonRoles}
      />

      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button
            Icon={ContentAdd}
            emphasis="high"
            onClick={createBtnOnClick}
            disabled={selectedPersonRoles.length === 0 || creatingEntities}
          >
            {t('action.create', 'Create')}
          </Button>
          <Button emphasis="medium" onClick={cancelBtnOnClick} disabled={creatingEntities}>
            {t('action.cancel', 'Cancel')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}

async function createTreeEntity(
  error,
  ok,
  t,
  templePlatform,
  setModifiedPersons,
  selectedPersonRoles,
  unselectedPersons,
  openModifyCtrDialog
) {
  const ordinanceType = getLastPathElement(templePlatform.ctrs[0].ordinanceType)
  const selectedPersons = selectedPersonRoles.map((role) => {
    return {
      role,
      person: templePlatform.persons.find(
        (person) => person.id === templePlatform.ctrs[0].ctrExtendedDetail[0][role.toLowerCase()].resourceId
      ),
    }
  })
  const findPid = (allPersons, role) => {
    const person = allPersons.find((p) => p.role === role)
    const personId =
      person.person.newPid || person.person.forwardedPid || getPidFromIdentifier(person.person.identifiers)
    return personId
  }
  replaceEmptyNameWithQuestionMarkInGivenName(selectedPersons)

  try {
    const treePersons = await Promise.all(
      selectedPersons.map(async (person) => {
        const tfPersonPayload = createTreePayload(person.person)
        return createTreeDataPerson(tfPersonPayload)
      })
    )
    try {
      const forwardedTreePersons = await Promise.all(
        unselectedPersons.map(async (person) => {
          return getPerson(getPidFromIdentifier(person.person.identifiers), true)
        })
      )
      for (let f = 0; f < forwardedTreePersons.length; f++) {
        if (forwardedTreePersons[f].tombstoned) {
          unselectedPersons[f].person.tombstoned = true
        }
        if (getPidFromIdentifier(unselectedPersons[f].person.identifiers) !== forwardedTreePersons[f].id) {
          unselectedPersons[f].person.forwardedPid = forwardedTreePersons[f].id
        }
      }
      if (treePersons && treePersons.length > 0) {
        treePersons.forEach((person, index) => {
          const newPid = person.data.id
          selectedPersons[index].person.newPid = newPid
        })
        const combinedPersons = selectedPersons.concat(unselectedPersons)
        const allPersons = templePlatform.persons
          .filter((templePlatformPerson) => !templePlatformPerson.id.includes('qr.'))
          .map((templePlatformPerson) => {
            return combinedPersons.find((person) => templePlatformPerson.id === person.person.id)
          })
        allPersons.forEach((person) => {
          updatePidsInCtrData(person.person, templePlatform.persons, person.role)
        })
        checkForInconsistentNewPids(t, allPersons, templePlatform.persons, error)

        try {
          if (selectedPersonRoles.includes('Wife')) {
            const principalId = findPid(allPersons, 'Principal')
            const wifeId = findPid(allPersons, 'Wife')
            await createTreeDataCouple(principalId, wifeId, 'FEMALE')
          }

          if (selectedPersonRoles.includes('Mother') || selectedPersonRoles.includes('Father')) {
            const principalId = findPid(allPersons, 'Principal')
            const fatherId = findPid(allPersons, 'Father')
            const motherId = findPid(allPersons, 'Mother')
            await createTreeDataParentChildRelationship(principalId, fatherId, motherId)
          }
          if (
            ordinanceType === 'SEALING_TO_SPOUSE' &&
            !selectedPersonRoles.includes('Mother') &&
            !selectedPersonRoles.includes('Father')
          ) {
            const principalId = findPid(allPersons, 'Principal')
            const wifeId = findPid(allPersons, 'Wife')
            await createTreeDataCouple(principalId, wifeId, 'FEMALE')
          }
          if (
            ordinanceType === 'SEALING_TO_PARENTS' &&
            !selectedPersonRoles.includes('Mother') &&
            !selectedPersonRoles.includes('Father')
          ) {
            const principalId = findPid(allPersons, 'Principal')
            const fatherId = findPid(allPersons, 'Father')
            const motherId = findPid(allPersons, 'Mother')
            await createTreeDataParentChildRelationship(principalId, fatherId, motherId)
          }
          ok(t('ctr.summary.createTreePerson.messages.success', 'Created persons and relationships for the CTR'))
          setModifiedPersons(allPersons)
          openModifyCtrDialog()
        } catch (err) {
          error(
            `${t('ctr.summary.createTreePerson.messages.errorCreatingTreeEntities', 'Error creating tree entities')}: ${
              err.message
            }`,
            err
          )
        }
      }
    } catch (err) {
      error(
        `${t(
          'ctr.summary.createTreePerson.messages.errorGettingForwardedIds',
          'Failed to get forwarded IDs'
        )}:' ${JSON.stringify(err.message)}`
      )
    }
  } catch (err) {
    error(
      `${t(
        'ctr.summary.createTreePerson.messages.errorCreatingTreePersons',
        'Failed to create tree person(s)'
      )}: ${JSON.stringify(err.message)}`
    )
  }
}

function replaceEmptyNameWithQuestionMarkInGivenName(persons) {
  const REPLACEMENT_CHAR = '?'

  const defaultGivenName = {
    type: 'http://gedcomx.org/Given',
    value: REPLACEMENT_CHAR,
  }

  persons.forEach((person) => {
    if (person.person.names && person.person.names.length > 0) {
      person.person.names.forEach((name) => {
        if (name.nameForms && name.nameForms.length > 0) {
          name.nameForms.forEach((form) => {
            if (!form.fullText || form.fullText.trim().length === 0) {
              form.fullText = REPLACEMENT_CHAR
              if (!form.parts) {
                form.parts = []
                form.parts.push(defaultGivenName)
              } else {
                let newText = ''
                form.parts.forEach((part) => {
                  newText += part.value.trim()
                })
                if (!newText || newText.length === 0) {
                  let found = false
                  form.parts.forEach((part) => {
                    if (part.type.indexOf('Given') !== -1) {
                      found = true
                      part.value = REPLACEMENT_CHAR
                    }
                  })
                  if (!found) {
                    form.parts.push(defaultGivenName)
                  }
                }
              }
            }
          })
        }
      })
    } else {
      const newNamesObject = [
        {
          nameForms: [
            {
              fullText: REPLACEMENT_CHAR,
              lang: 'und',
              parts: [
                {
                  type: 'http://gedcomx.org/Given',
                  value: REPLACEMENT_CHAR,
                },
              ],
            },
          ],
        },
      ]
      person.person.names = newNamesObject
    }
  })
}

function checkForInconsistentNewPids(t, persons, ctrPersons, error) {
  const memory = {}
  persons.forEach((person) => {
    const personRelOldPid = person.person.dmc2OldPid
    const personRelNewPid = person.person.newPid
    const usysId = personRelNewPid || personRelOldPid
    const ctrPersonsFiltered = getCtrPersonsByUsysId(usysId, ctrPersons, false)
    if (ctrPersonsFiltered.length === 0) {
      error(
        `${t(
          'ctr.summary.createTreePerson.messages.errorFindingCtrPerson',
          'Did not find any ctrPerson with UsysId'
        )}: ${usysId}`
      )
    }
    if (ctrPersonsFiltered.length >= 2) {
      error(
        t(
          'ctr.summary.createTreePerson.messages.errorFindingTooManyCtrPersons',
          'Found { ctrPersonsCount } ctrPerson with UsysId: { usysId }',
          {
            ctrPersonsCount: ctrPersonsFiltered.length,
            usysId,
          }
        )
      )
      error(
        `${t('ctr.summary.createTreePerson.messages.errorFindingCtrPerson', 'Did not find any ctrPerson with UsysId')}`
      )
    }
    const ctrPerson = ctrPersonsFiltered[0]
    const ctrDataPid = ctrPerson ? getPidFromIdentifier(ctrPerson.identifiers).replace('p.', '') : null
    const ctrDataPidIsOldPid = ctrDataPid === personRelOldPid
    const ctrDataPidIsNewPid = ctrDataPid === personRelNewPid
    const personWasSelected = personRelNewPid
    const newPidsAreDifferent = personRelNewPid !== ctrDataPid
    const newPidIsRepeated = !!memory[ctrDataPid]
    const notificationMessage = t(
      'ctr.summary.createTreePerson.messages.errorInconsistentData',
      '{ role } data is inconsistent. Do not continue. PersonRelNewPid: { personRelNewPid }, personRelOldPid: { personRelOldPid } ctrDataUsysId: { ctrDataPid }',
      {
        role: person.role,
        personRelNewPid,
        personRelOldPid,
        ctrDataPid,
      }
    )
    if (!personWasSelected && (ctrDataPidIsNewPid || !ctrDataPidIsOldPid)) {
      error(notificationMessage)
    }
    if (personWasSelected && (newPidsAreDifferent || newPidIsRepeated || ctrDataPidIsOldPid)) {
      error(notificationMessage)
    }
    memory[ctrDataPid] = true
  })
}

function updatePidsInCtrData(person, ctrPersons, role) {
  const ctrPersonsFiltered = getCtrPersonsByUsysId(getPidFromIdentifier(person.identifiers), ctrPersons, true)
  ctrPersonsFiltered.forEach((ctrPerson) => {
    ctrPerson.dmc2Role = role
    ctrPerson.dmc2OldPid = getPidFromIdentifier(ctrPerson.identifiers)
    if (person.newPid) {
      ctrPerson.dmc2NewPid = person.newPid
      setIdInIdentifiers(ctrPerson.identifiers, `p.${person.newPid}`, 'http://api.familysearch.org/temple/USYS_ID')
    }
  })
}

function getCtrPersonsByUsysId(usysId, ctrPersons, includeQRIds) {
  if (!usysId || !ctrPersons) {
    return []
  }
  const found = []
  ctrPersons.forEach((ctrPerson) => {
    if (
      (includeQRIds || !ctrPerson.id.includes('qr.')) &&
      isSameId(usysId, getPidFromIdentifier(ctrPerson.identifiers))
    ) {
      found.push(ctrPerson)
    }
  })
  return found
}

function isSameId(id1, id2) {
  const findIdStartIdx = (id) => {
    return id.indexOf('.') + 1
  }
  return id1.substring(findIdStartIdx(id1)) === id2.substring(findIdStartIdx(id2))
}

function createTreePayload(person) {
  const tfNames = mapTfNames(person)
  const tfFacts = mapTfFacts(person)
  return {
    names: tfNames,
    facts: tfFacts,
    gender: {
      value: {
        type: person?.gender?.type || 'Undefined',
      },
    },
  }
}

function mapTfNames(person) {
  const tfNames = []
  person.names &&
    person.names.length > 0 &&
    person.names.forEach((name) => {
      tfNames.push({
        value: {
          type: 'http://gedcomx.org/BirthName',
          nameForms: getTfNameForms(name),
        },
      })
    })
  if (!!tfNames && tfNames.length > 0) {
    tfNames[0].preferred = true
  }
  return tfNames
}

function getTfNameForms(name) {
  return name.nameForms.map((nameForm) => {
    return {
      lang: nameForm.lang,
      order: findNameFormOrder(nameForm),
      parts: nameForm.parts,
    }
  })
}

function findNameFormOrder(nameForm) {
  let order = null
  const exts = nameForm['http://api.familysearch.org/temple/scriptExtensions']
  if (exts && exts.length > 0) {
    exts.forEach((ext) => {
      if (ext.nameOrder) {
        order = ext.nameOrder
      }
    })
  }
  return order
}

function mapTfFacts(person) {
  const qualifyingFactTypes = ['Birth', 'Christening', 'Death', 'Burial']

  if (!person.facts) {
    person.facts = []
  }
  const personRelFacts = person.facts
  if (!hasDeathEvent(personRelFacts, qualifyingFactTypes)) {
    personRelFacts.push({
      type: 'http://gedcomx.org/Death',
    })
  }

  const tfFacts = []
  const qualifyingEvents = getQualifyingEvents(personRelFacts, qualifyingFactTypes)
  qualifyingEvents &&
    qualifyingEvents.forEach((fact) => {
      const tfFact = deepCopy(fact)
      if (tfFact.place && !tfFact.place.original) {
        delete tfFact.place
      }
      if (tfFact.date && !tfFact.date.original) {
        delete tfFact.date
      }
      delete tfFact.id
      tfFacts.push({
        value: tfFact,
      })
    })
  return tfFacts
}

function hasDeathEvent(facts, qualifyingFactTypes) {
  let isFound = false
  facts &&
    facts.length > 0 &&
    facts.forEach((fact) => {
      const idx = getIdxOfEventString(fact)
      if (fact.type.substring(idx) === qualifyingFactTypes[2]) {
        isFound = true
      }
    })
  return isFound
}

function getQualifyingEvents(facts, qualifyingFactTypes) {
  return facts.filter((fact) => {
    const idx = getIdxOfEventString(fact)
    if (idx > 0 && isQualifyingFact(fact, idx, qualifyingFactTypes)) {
      return fact
    }
    return undefined
  })
}

function isQualifyingFact(fact, idx, qualifyingFactTypes) {
  const foundType = qualifyingFactTypes.filter((type) => type === fact.type.slice(idx))
  return foundType && foundType.length > 0
}

function getIdxOfEventString(fact) {
  return fact.type.lastIndexOf('/') + 1
}
